import React, { useState, useEffect } from 'react';
import { addDays, format } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { DateRangePicker } from 'react-date-range';
import {
  Box,
  Input,
  InputAdornment,
  IconButton,
  styled,
} from '@material-ui/core';

import { ReactComponent as IconCalendar } from '../../../assets/icons/icon-calendar.svg';
import { ReactComponent as IconArrowDown } from '../../../assets/icons/icon-arrow-down.svg';
import { ReactComponent as IconArrowUp } from '../../../assets/icons/icon-arrow-up.svg';
import './styles.css';
import colors from '../../../assets/styles/colors';
import commons from '../../../assets/styles/commons';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import ptBR from 'date-fns/locale/pt-BR';
import { formatDate } from '../../../utils/formatTime';
import { date } from 'yup';

interface IPeriod {
  startDate?: Date;
  endDate?: Date;
}

interface CustomizedInputsProps {
  label: string;
  id?: string;
  style?: React.CSSProperties;
  value: IPeriod;
  placeholder?: string;
  onChange?: (value: Required<IPeriod>) => void;
  isPickerOpen: boolean;
  setIsPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const InputStyle = styled(Input)(({ theme }) => ({
  'label + &': {
    color: colors.neutral.gray,
  },

  '&.MuiInputBase-root': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: colors.secondary.softBlue,
    border: '1px solid',
    borderColor: colors.primary.orange,
    flex: 1,
    height: 40,
    width: 'auto',
    padding: '10px 12px',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    fontFamily: commons.fontFamily,
    '&:focus': {
      boxShadow: `rgba(255,114,6,0.25) 0 0 0 0.2rem`,
      borderColor: colors.primary.orange,
    },
    '&:error': {},
  },

  '& .MuiInputBase-input': {
    color: colors.neutral.gray,
  },
}));

const DateRangerCustom: React.FC<CustomizedInputsProps> = ({
  label,
  id,
  style,
  value,
  isPickerOpen,
  setIsPickerOpen,
  placeholder,
  onChange,
}) => {
  const [state, setState] = useState([
    {
      startDate: value.startDate ? new Date(value.startDate) : new Date(),
      endDate: value.endDate ? new Date(value.endDate) : addDays(new Date(), 7),
      key: 'selection',
    },
  ]);

  const [isArrowRotated, setIsArrowRotated] = useState(false);

  const handleTogglePicker = () => {
    setIsPickerOpen(!isPickerOpen);
    setIsArrowRotated(!isArrowRotated);
  };

  useEffect(() => {
    const weekDays = document.querySelectorAll('.rdrWeekDay');
    weekDays.forEach(day => {
      const fullDay = day.textContent?.toLowerCase();
      if (fullDay) {
        const abbreviatedDay = getAbbreviatedDay(fullDay);
        day.textContent = abbreviatedDay;
      }
    });
  }, [isPickerOpen]);

  const getAbbreviatedDay = (day: string): string => {
    const daysMap: Record<string, string> = {
      domingo: 'Dom',
      segunda: 'Seg',
      terça: 'Ter',
      quarta: 'Qua',
      quinta: 'Qui',
      sexta: 'Sex',
      sábado: 'Sáb',
    };

    return daysMap[day] || day;
  };

  useEffect(() => {
    const updateStaticRangesLabels = () => {
      const staticRanges = document.querySelectorAll('.rdrStaticRange');
      staticRanges.forEach((range, index) => {
        const labelElement = range.querySelector('.rdrStaticRangeLabel');
        if (labelElement) {
          const label = labelElement.textContent?.toLowerCase();
          if (label) {
            const translatedLabel = getTranslatedLabel(label);
            labelElement.textContent = translatedLabel;
          }
        }
      });
    };

    updateStaticRangesLabels();
  }, [isPickerOpen]);

  const getTranslatedLabel = (label: string): string => {
    const labelsMap: Record<string, string> = {
      today: 'Hoje',
      yesterday: 'Ontem',
      'this week': 'Esta Semana',
      'last week': 'Semana Passada',
      'this month': 'Este Mês',
      'last month': 'Mês Passado',
    };

    return labelsMap[label] || label;
  };

  const Label = styled('div')({
    color: colors.primary.orange,
    fontFamily: commons.fontFamily,
    marginBottom: 8,
    fontSize: '13px',
  });

  return (
    <Box
      component={'div'}
      className="custom-date-ranger"
      sx={{
        display: 'grid',
      }}
    >
      <DemoItem label={<Label>{label}</Label>} children={undefined}></DemoItem>
      <InputStyle
        disableUnderline
        placeholder={
          isPickerOpen
            ? 'Selecione as datas'
            : placeholder || 'Clique para selecionar as datas'
        }
        fullWidth
        value={` ${format(state[0].startDate, 'dd/MM/yy')} - ${format(
          state[0].endDate,
          'dd/MM/yyyy',
        )}`}
        readOnly
        startAdornment={
          <InputAdornment position="start">
            <IconCalendar />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end">
            <IconButton onClick={handleTogglePicker}>
              {isArrowRotated ? <IconArrowUp /> : <IconArrowDown />}
            </IconButton>
          </InputAdornment>
        }
      />

      {isPickerOpen && (
        <DateRangePicker
          onChange={(item: {
            selection: { startDate: Date; endDate: Date; key: string };
          }) => {
            if (onChange) {
              onChange(item.selection);
              setState([item.selection]);
            }
          }}
          showSelectionPreview={true}
          moveRangeOnFirstSelection={false}
          months={2}
          ranges={state}
          direction="horizontal"
          rangeColors={[colors.primary.lightOrange]}
          color={colors.primary.lightOrange}
          onClose={() => setIsPickerOpen(false)}
          locale={ptBR}
        />
      )}
    </Box>
  );
};

export default DateRangerCustom;
